<template>
  <v-container class="pt-0">
    <v-card>
      <v-row class="px-4">
        <v-col cols="3">
          <v-card-title class="pa-0">
            Export Attendance
          </v-card-title>
        </v-col>
        <v-spacer />
      </v-row> 

      <v-row class="px-4">
        <v-col
          cols="6"
          class="pt-0"
        >
          <v-data-table 
            v-model="selectedEmployees"
            v-validate="'required'"
            dense
            :error-messages="errors.collect('selectedEmployees')"
            data-vv-name="selectedEmployees"
            data-vv-as="employees"
            :loading="tableLoading"
            :headers="headers"
            :items="filteredEmployees"
            hide-default-footer
            fixed-header
            disable-pagination
            show-select
            item-key="employee_uid"
            item-value="employee_uid"
            class="elevation-2 mb-3"
            height="330px"
          >
            <template v-slot:top>
              <h5 class="font-weight-bold py-2 mb-0 pl-2">
                Employee Selection:
              </h5>
            </template>
          </v-data-table>
          <span class="red--text text--lighten-1 caption">{{ errors.first('selectedEmployees') }}</span>
        </v-col>

        <v-col
          cols="6"
          class="pt-0"
        >
          <v-text-field
            v-model="searchInput"
            placeholder="Filter employee by name"
            append-icon="search"
            label="Search Employee"
            hide-no-data
            hide-selected
          />

          <v-autocomplete
            v-model="filters.positions"
            :items="positions"
            item-text="label"
            item-value="id"
            value="id"
            label="Position"
            placeholder="Filter employee by position"
            clearable
            hide-selected
            multiple
            small-chips
            deletable-chips
            chips
          />

          <v-autocomplete
            v-model="filters.departments"
            :items="departments"
            item-text="name"
            item-value="id"
            value="id"
            label="Department"
            placeholder="Filter employee by department"
            clearable
            hide-selected
            multiple
            deletable-chips
            small-chips
            chips
          />

          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDateDisplay"
                v-validate="'required'"
                label="Start Date"
                prepend-icon="mdi-calendar"
                :error-messages="errors.collect('startDateDisplay')"
                data-vv-name="startDateDisplay"  
                data-vv-as="start date"    
                readonly
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="startDate"
              :max="endDate || today"
              @input="startDateMenu = false"
            />
          </v-menu>

          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="endDateDisplay"
                v-validate="'required'"
                label="End Date"
                prepend-icon="mdi-calendar"
                :error-messages="errors.collect('endDateDisplay')"
                data-vv-name="endDateDisplay"  
                data-vv-as="end date"  
                readonly
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="endDate"
              :min="startDate"
              :max="today"
              @input="endDateMenu = false"
            />
          </v-menu>

          <v-card-actions>
            <v-spacer />
            <v-btn
              v-if="$hasPermissionTo(['export attendances'])"
              block
              :loading="progressing"
              color="hrissecondary"     
              class="white--text"     
              @click="submitForm"
            >
              Export
            </v-btn>
          </v-card-actions>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        progressing: false,
        tableLoading: false,
        searchInput: '',
        employees: [],
        filters: {
          positions: '',
          departments: '',
        },
        positions: [],
        departments: [],
        selectedEmployees: [],
        startDateMenu: false,
        startDate: this.$moment().format('YYYY-MM-DD'),
        endDateMenu: false,
        endDate: this.$moment().format('YYYY-MM-DD'),
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'person.name',
          },
          {
            text: 'Department',
            align: 'left',
            sortable: true,
            value: 'department.name',
          },
          {
            text: 'Position',
            align: 'left',
            sortable: true,
            value: 'current_position.position.name',
          }, 
          {
            text: 'Resigned Date',
            align: 'left',
            sortable: true,
            value: 'end_date',
          },       
        ],
        today: new Date().toISOString().slice(0, 10), 
      }
    },
    computed: {
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.endDate ? this.$moment(this.endDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      filteredEmployees () {
        let _this = this
        _this.searchInput = _this.searchInput.trim()
        let primaryFilterEmployees = this.employees.filter(function (employee) {
          return ((employee.person || {}).name || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1 ||
            ((employee.person || {}).username || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1
        })

        let filteredPositions = this.customFilterPositions(primaryFilterEmployees)
        let filteredStates = this.customFilterDepartments(filteredPositions)
        return filteredStates
      },
    },
    watch: {
      filteredEmployees (newList) {
        this.$validator.pause()

        const newEmployeeUids = new Set(newList.map(emp => emp.employee_uid))

        this.selectedEmployees = this.selectedEmployees.filter(selectedEmp =>
          newEmployeeUids.has(selectedEmp.employee_uid)
        )

        this.$nextTick(() => {
          this.$validator.resume()
        })
      },
    },
    created () {
      this.tableLoading = true

      Promise.all([
        this.$api.employees().list('?with=attendance'),
        this.$api.positions().list(),
        this.$api.hrisDepartments().list(['name'], false),
      ])
        .then(([employees, positions, departments]) => {
          let temp = employees.data.filter(employee => {
            return employee.department_id !== null
          })

          this.employees = _.orderBy(temp, ['end_date', 'person.name', 'department.name'], ['desc', 'asc', 'asc'])
          this.positions = positions.data.map(pos => ({
            id: pos.id,
            label: `${pos.name} (${pos.job_grade.job_group.name})`,
          }))
          this.departments = departments.data

          this.tableLoading = false
        })
    },
    methods: {
      prepareFormData () {
        return {
          employeeUids: this.getEmployeeUids(),
          date_from: this.startDate,
          date_to: this.endDate,
        }
      },   
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true
          
            this.$api.attendances().export(this.prepareFormData())
              .then((res) => {
                let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'Attendance Report.xlsx')
                link.click()
                this.progressing = false

                this.$snack({ message: 'Report downloaded successfully' })           
              })
              .catch(() => {
                this.progressing = false
                this.$snack({ 
                  color: 'error',
                  message: 'Failed to download attendances. Please contact IT support', 
                })
              })
          })
      },

      getEmployeeUids () {
        let employeeUids = []

        this.selectedEmployees.map(item => {
          employeeUids.push(item.employee_uid)
        })

        return employeeUids
      },

      customFilterPositions (employeesList) {
        if (this.filters.positions.length === 0) return employeesList

        return employeesList.filter(employee => {
          let positionId = (employee.current_position || {}).position_id
  
          return _.intersection([positionId], this.filters.positions).length > 0
        })
      },

      customFilterDepartments (employeesList) {
        if (this.filters.departments.length === 0) return employeesList

        return employeesList.filter(employee => {
          return _.intersection([employee.department_id], this.filters.departments).length > 0
        })
      },
    },
  }
</script>
